import React, {useState, useEffect} from 'react';
import {IProps, IState} from './types';
import * as S from './styles';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@state/actions';
import selectors from '@state/selectors';
import Tabs from '@components/Tabs';
import Loading from '@components/Loading';
import EmptyTemplateIcon from '@assets/icons/emptyTemplateIcon';
import { IDSConfig } from '@state/dsconfig/types';
import { ITemplate } from '@state/templates/types';
import CarruselTemplates from '../CarruselTemplates';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { hasMinimalPermissions } from '@utils/userAccess';

const TEMPLATES_OEM_LENGTH = 15;

const TabTemplates:React.FC<IProps> = (props) => {
  const authContext = React.useContext(AuthContext);
  const userData = authContext && authContext.userData?authContext.userData.profile:undefined;
  const dispatch = useDispatch();
  const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig); 
  const templates = useSelector(selectors.templates.getEveryTemplates);
  const hasMounted = React.useRef(false);
  
  const fetchEveryTemplates = (option: string, OEMId: string) => {
    
    let query = createQuery(option, OEMId);
    return new Promise<ITemplate[]>((resolve, reject) => {
      dispatch(actions.templates.fetchEveryTemplates(query, (result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const createQuery=(option: string, OEMId: string)=>{
    let designType = option!=="" ? `c.tags.designType=%27${option}%27 and` : ``;
    var finalQuery = `?filter=${designType} c.templateStatus=%27PUBLISHED%27 and c.tags.oem=%27${OEMId}%27&userdata=${JSON.stringify(userData)}&all=true&orderby=c.updateDate DESC&start=0&pagelength=${TEMPLATES_OEM_LENGTH}`;
    return finalQuery;
  }

  const [options, setOptions] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [currentTemplates, setCurrentTemplates] = useState<IState['currentTemplates']>([]);
  const [globalTemplates, setGlobalTemplates] = useState<IState['globalTemplates']>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [indexType, setIndexType] = useState(0);
  const [hasMore, setHasmore] = useState<IState['hasMore']>([]);
  const [hasTemplates, setHasTemplates] = useState(false);

  const loadMoreTemplates = (needMore: boolean, tagOption: string) => {    
    if (needMore) dsConfig?.brands.map(OEM => fetchEveryTemplates(tagOption, OEM.id ?? OEM.name));
  }

  const createOptions = () => {
    var newOptions: string[] = [];
    var newTagOptions: string[] = [];
    var newHasMore: boolean[] = [];
  
    if (dsConfig?.designTypes && dsConfig.designTypes.length > 0) {
      setIsLoading(true);
  
      dsConfig.designTypes.map((type: any, index: number) => {
        newTagOptions.push(type.name);
        newOptions.push((type.name.split("/").length > 0 ? type.name.split("/")[0] : type.name).toUpperCase());
        newHasMore.push(true);
      });
  
      const defaultTab = !hasMinimalPermissions(authContext.userData?.profile,'defaultTemplate')
        ? newOptions.find(opt => opt === "WEBSITE") ?? newOptions[0]
        : newOptions[0];
  
      const defaultIndex = newOptions.indexOf(defaultTab);
      if (defaultIndex >= 0) newHasMore[defaultIndex] = false;
  
      setIndexType(defaultIndex);
      loadMoreTemplates(true, newTagOptions[defaultIndex]);
      
      setHasmore([...newHasMore]);
      setTagOptions(newTagOptions);
      setOptions(newOptions);
      setValue(defaultTab);
    }
  }
  

  useEffect(() => {
    createOptions();
  }, [dsConfig]);

  useEffect(() => {
    if (templates) {
      
      let validIndex = -1;
      if (templates.length > 0) {
        validIndex = tagOptions.findIndex(tagOption => tagOption === templates[0].tags.designType);
        if (!hasTemplates) setHasTemplates(true);
        if (validIndex >= 0) {
          let newGlobalTemplates = globalTemplates?.length ? [...globalTemplates] : Array(dsConfig.designTypes.length).fill([]);
          newGlobalTemplates[validIndex] = newGlobalTemplates[validIndex].concat([...templates]);
          setGlobalTemplates([...newGlobalTemplates]);
          if (validIndex === indexType) setCurrentTemplates([...newGlobalTemplates[validIndex]]);
        }
      }
      if (validIndex === -1) validIndex = indexType;
      if (hasMore?.length && hasMore[validIndex]) {
        setTimeout(() => {
          if (hasMore?.length && hasMore[validIndex]) {
            setHasmore([
              ...hasMore.slice(0, validIndex),
              false,
              ...hasMore.slice(validIndex + 1)
            ]);
          }
        }, 1000);
      }
      if (value !== undefined && isLoading) setIsLoading(false);
    }
  }, [templates]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const valueIndex = options.findIndex((option: string) => (option === value));
    
    if (value && valueIndex >= 0 && hasMore.length && tagOptions.length && globalTemplates.length) {
      setCurrentTemplates(globalTemplates[valueIndex]);
      loadMoreTemplates(hasMore[valueIndex], tagOptions[valueIndex]);
      setIndexType(valueIndex);
    }
  }, [value]);

  const addRecentDesign = (index: number) => {
    props.addDesign(currentTemplates[index]);
  }

  const renderLoaderContainer = () => {
    return(
      <S.LoaderContainer>
        <Loading 
          id={"loading-designs"}
          containerStyle={{justifyContent: 'center'}}
        />
      </S.LoaderContainer>
    )
  }

  return (
    <S.Root>
      {
        isLoading ?
          <S.AllLoaderContainer>
            <Loading 
              id={"loading-designs"}
              containerStyle={{justifyContent: 'center'}}
            />
          </S.AllLoaderContainer>
        : !hasTemplates ?
          <S.NoTemplatesContainer>
            <S.EmptyIcon>
              <EmptyTemplateIcon/>
            </S.EmptyIcon>
            <S.EmptyTextContainer>
              <S.EmptyTitle>
                No templates added yet
              </S.EmptyTitle>
              <S.EmptySubtitle>
                Upload templates and manage them here. You'll be able to see things like size status, and the name of the file, among other properties.
              </S.EmptySubtitle>
            </S.EmptyTextContainer>
          </S.NoTemplatesContainer>
        :
          <>
            <S.Title>
              Templates
            </S.Title>
            <Tabs
              options={options}
              value={value ?? ""}
              setValue={setValue}
              sizes={{tabWidth: 150, barWidth: 100}}
            />
            {!hasMinimalPermissions(authContext.userData?.profile,'tabTemplates') && ['SOCIAL', 'DISPLAY'].includes(value!) ? (
              <S.UpsellWrapper>
                <S.UpsellContainer>
                  <S.UpsellTextContainer>
                    <S.UpsellTitle>
                      Unlock more creative power in Design Studio!
                    </S.UpsellTitle>
                    <S.UpsellDescription>
                      Right now, you have access to Website Designs — but there’s so much more you can create!
                    </S.UpsellDescription>
                    <S.FeatureList>
                      <S.FeatureItem>
                        <span style={{fontWeight: 'bold'}}>- Display Ads</span> – Design stunning, ready-to-launch display banners.</S.FeatureItem>
                      <S.FeatureItem>
                        <span style={{fontWeight: 'bold'}}>- Social Media Graphics</span> – Craft engaging social content to boost your brand.</S.FeatureItem>
                    </S.FeatureList>
                    <S.UpsellCTA>
                      Upgrade today to unlock access to hundreds of Display & Social templates to expand your design capabilities!
                    </S.UpsellCTA>
                    <S.UpsellCTA>
                      Contact your DealerOn representative to get full access.
                    </S.UpsellCTA>
                  </S.UpsellTextContainer>
                </S.UpsellContainer>

                <S.BlurredContent>
                  <S.TemplatesContainer>
                    { dsConfig?.brands.map((OEM, index) => {
                        return(
                          <CarruselTemplates
                            key={index}
                            OEM={OEM}
                            templates={currentTemplates.slice(0, 2)?.filter(template => template.tags.oem === (OEM.id ?? OEM.name))}
                            addRecentDesign={addRecentDesign}
                          />
                        );
                      })
                    }
                  </S.TemplatesContainer>
                </S.BlurredContent>
              </S.UpsellWrapper>
            ) : (
              <S.TemplatesContainer>
                { dsConfig?.brands.map((OEM, index) => {
                    return(
                      <CarruselTemplates
                        key={index}
                        OEM={OEM}
                        templates={currentTemplates?.filter(template => template.tags.oem === (OEM.id ?? OEM.name))}
                        addRecentDesign={addRecentDesign}
                      />
                    );
                  })
                }
                { (hasMore.length > 0 && currentTemplates.length === 0) &&
                    (!hasMore[indexType] ?
                      <S.NoTabTemplatesContainer>
                        <S.TabEmptyIcon>
                          <EmptyTemplateIcon />
                        </S.TabEmptyIcon>
                        <S.TabEmptyTextContainer>
                          There are no templates in this category yet
                        </S.TabEmptyTextContainer>
                      </S.NoTabTemplatesContainer>
                    :
                      renderLoaderContainer()
                  )
                }
              </S.TemplatesContainer>
            )}
            
          </>
      }
    </S.Root >
  );
}

export default TabTemplates;